import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@parrot-software/design-system';
import { MoreHorizontal, Trash2, Copy } from '@parrot-software/design-system/icons';
import type { Role } from '@personnel/domain/entities';
import { cn } from '@parrot-software/utils';
/** Name of the special super admin role that cannot be edited, duplicated or deleted */
const SUPER_ADMIN_ROLE_NAME = 'Super Admin';

interface RolesTableRowProps {
  role: Role;
  onDelete?: (roleId: string) => void;
}

/**
 * Roles Table Row Component
 *
 * Single row in the roles table with actions menu.
 * Uses Semantic Styles Pattern for maintainability.
 *
 * @param {RolesTableRowProps} props - Component properties
 * @returns {React.ReactElement} Roles table row component
 */
const styles = {
  row: 'border-b border-border flex items-center h-[52px] px-2 w-full',
  cell: 'flex-1 px-2 min-w-0',
  cellRight: 'flex-1 px-2 text-right min-w-0',
  cellActions: 'flex-1 px-2 flex justify-end min-w-0',
  text: 'text-sm text-primary truncate font-medium',
  textCenter: 'text-sm text-foreground',
  actionButton: 'size-8',
  icon: 'size-4',
  actionMenuItem: 'cursor-pointer',
};

export const RolesTableRow = ({ role, onDelete }: RolesTableRowProps): React.ReactElement => {
  const { t } = useTranslation('roles');
  const history = useHistory();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  /** Check if this is the special super admin role */
  const isSuperAdmin = role.name === SUPER_ADMIN_ROLE_NAME;

  const handleDuplicate = () => {
    history.push(`/roles/${role.uuid}/duplicate`);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(role.uuid);
    }
  };

  const handleRowClick = () => {
    if (!isSuperAdmin) {
      history.push(`/roles/${role.uuid}`);
    }
  };

  return (
    <div className={styles.row}>
      {/* Role Name */}
      <div className={styles.cell}>
        {isSuperAdmin ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className={cn(styles.text, 'hover:no-underline pl-0')} variant="link">
                  {role.name}
                </Button>
              </TooltipTrigger>
              <TooltipContent
                align="start"
                className="max-w-md bg-background text-foreground px-3 py-1.5 rounded-md shadow-md text-sm leading-5"
                side="bottom"
              >
                <p>{t('superAdmin.tooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button className={cn(styles.text, 'pl-0')} onClick={handleRowClick} variant="link">
            {role.name}
          </Button>
        )}
      </div>

      {/* Users Count */}
      <div className={styles.cellRight}>
        <p className={styles.textCenter}>{role.usersCount ?? 0}</p>
      </div>

      {/* Actions - hidden for global admin */}
      <div className={styles.cellActions}>
        {!isSuperAdmin && (
          <DropdownMenu onOpenChange={setIsMenuOpen} open={isMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button className={styles.actionButton} size="icon" type="button" variant="ghost">
                <MoreHorizontal className={styles.icon} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className={styles.actionMenuItem} onClick={handleDuplicate}>
                <Copy className={styles.icon} />
                {t('actions.duplicate')}
              </DropdownMenuItem>
              <DropdownMenuItem className={styles.actionMenuItem} onClick={handleDelete}>
                <Trash2 className={styles.icon} />
                {t('actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
